/**
 * Agent Settings Page
 *
 * Manage OpenClaw agent connections and API keys.
 */

import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import PageHeader from '../components/PageHeader';
import AgentSettingsService, {
  AgentConnection,
  ApiKey,
  ApiKeyCreateResult,
} from '../services/AgentSettingsService';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import VpnKeyIcon from '@mui/icons-material/VpnKey';
import './AgentSettingsPage.css';

const service = AgentSettingsService.getInstance();

export const AgentSettingsPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  // Agent connections
  const [connections, setConnections] = useState<AgentConnection[]>([]);
  const [connectionsLoading, setConnectionsLoading] = useState(true);
  const [showAddConnection, setShowAddConnection] = useState(false);
  const [editingConnection, setEditingConnection] = useState<string | null>(null);
  const [connectionForm, setConnectionForm] = useState({ name: '', agent_name: 'iris' });
  const [testResults, setTestResults] = useState<Record<string, { success: boolean; latencyMs?: number; error?: string }>>({});
  const [testingId, setTestingId] = useState<string | null>(null);

  // API keys
  const [apiKeys, setApiKeys] = useState<ApiKey[]>([]);
  const [keysLoading, setKeysLoading] = useState(true);
  const [newKey, setNewKey] = useState<ApiKeyCreateResult | null>(null);
  const [newKeyName, setNewKeyName] = useState('');
  const [copiedKeyId, setCopiedKeyId] = useState<string | null>(null);

  const [activeTab, setActiveTab] = useState<'connections' | 'apikeys'>('connections');

  useEffect(() => {
    if (!user) navigate('/');
  }, [user, navigate]);

  const loadConnections = useCallback(async () => {
    try {
      setConnectionsLoading(true);
      const data = await service.listConnections();
      setConnections(data);
    } catch (err) {
      console.error('Failed to load connections:', err);
    } finally {
      setConnectionsLoading(false);
    }
  }, []);

  const loadApiKeys = useCallback(async () => {
    try {
      setKeysLoading(true);
      const data = await service.listApiKeys();
      setApiKeys(data);
    } catch (err) {
      console.error('Failed to load API keys:', err);
    } finally {
      setKeysLoading(false);
    }
  }, []);

  useEffect(() => {
    loadConnections();
    loadApiKeys();
  }, [loadConnections, loadApiKeys]);

  // --- Connection handlers ---

  const handleAddConnection = async () => {
    if (!connectionForm.name.trim()) return;
    try {
      await service.createConnection({
        name: connectionForm.name.trim() || undefined,
        agent_name: connectionForm.agent_name.trim() || undefined,
      });
      setConnectionForm({ name: '', agent_name: 'iris' });
      setShowAddConnection(false);
      loadConnections();
    } catch (err: any) {
      console.error('Failed to create connection:', err);
    }
  };

  const handleUpdateConnection = async (id: string) => {
    if (!connectionForm.name.trim()) return;
    try {
      await service.updateConnection(id, {
        name: connectionForm.name.trim() || undefined,
        agent_name: connectionForm.agent_name.trim() || undefined,
      });
      setEditingConnection(null);
      setConnectionForm({ name: '', agent_name: 'iris' });
      loadConnections();
    } catch (err: any) {
      console.error('Failed to update connection:', err);
    }
  };

  const handleDeleteConnection = async (id: string) => {
    try {
      await service.deleteConnection(id);
      loadConnections();
    } catch (err: any) {
      console.error('Failed to delete connection:', err);
    }
  };

  const handleTestConnection = async (id: string) => {
    setTestingId(id);
    try {
      const result = await service.testConnection(id);
      setTestResults(prev => ({ ...prev, [id]: result }));
    } catch (err: any) {
      setTestResults(prev => ({ ...prev, [id]: { success: false, error: err.message } }));
    } finally {
      setTestingId(null);
    }
  };

  const startEditing = (conn: AgentConnection) => {
    setEditingConnection(conn.id);
    setConnectionForm({ name: conn.name, agent_name: conn.agent_name || 'iris' });
  };

  // --- API key handlers ---

  const handleCreateKey = async () => {
    try {
      const result = await service.createApiKey(newKeyName.trim() || undefined);
      setNewKey(result);
      setNewKeyName('');
      loadApiKeys();
    } catch (err: any) {
      console.error('Failed to create API key:', err);
    }
  };

  const handleRevokeKey = async (id: string) => {
    try {
      await service.revokeApiKey(id);
      loadApiKeys();
    } catch (err: any) {
      console.error('Failed to revoke key:', err);
    }
  };

  const handleCopyKey = (key: string, id: string) => {
    navigator.clipboard.writeText(key);
    setCopiedKeyId(id);
    setTimeout(() => setCopiedKeyId(null), 2000);
  };

  return (
    <div className="agent-settings-page">
      <PageHeader />

      <div className="agent-settings-container">
        <div className="agent-settings-header">
          <SmartToyIcon sx={{ fontSize: 28 }} />
          <h1>Agent Settings</h1>
        </div>

        <div className="agent-settings-tabs">
          <button
            className={`agent-tab ${activeTab === 'connections' ? 'active' : ''}`}
            onClick={() => setActiveTab('connections')}
          >
            <SmartToyIcon sx={{ fontSize: 16 }} />
            Agent Connections
          </button>
          <button
            className={`agent-tab ${activeTab === 'apikeys' ? 'active' : ''}`}
            onClick={() => setActiveTab('apikeys')}
          >
            <VpnKeyIcon sx={{ fontSize: 16 }} />
            API Keys
          </button>
        </div>

        {/* Agent Connections Tab */}
        {activeTab === 'connections' && (
          <div className="agent-settings-section">
            <div className="section-header">
              <div>
                <h2>Agent Connections</h2>
                <p className="section-description">
                  Connect your OpenClaw agents to Knods. Agents can build flows, place nodes, and interact with your canvas.
                </p>
              </div>
              <button
                className="agent-add-btn"
                onClick={() => {
                  setShowAddConnection(true);
                  setConnectionForm({ name: '', agent_name: 'iris' });
                }}
              >
                <AddIcon sx={{ fontSize: 16 }} />
                Add Connection
              </button>
            </div>

            {/* Add/Edit Form */}
            {(showAddConnection || editingConnection) && (
              <div className="agent-form">
                <div className="agent-form-fields">
                  <div className="form-field">
                    <label>Display Name</label>
                    <span className="form-field-hint">A friendly name shown in the chat selector</span>
                    <input
                      type="text"
                      placeholder="e.g. Iris Claw, My Code Bot"
                      value={connectionForm.name}
                      onChange={e => setConnectionForm(prev => ({ ...prev, name: e.target.value }))}
                    />
                  </div>
                  <div className="form-field">
                    <label>Agent Name</label>
                    <span className="form-field-hint">The OpenClaw agent identifier to route messages to</span>
                    <input
                      type="text"
                      placeholder="e.g. iris, coder, researcher"
                      value={connectionForm.agent_name}
                      onChange={e => setConnectionForm(prev => ({ ...prev, agent_name: e.target.value }))}
                    />
                  </div>
                </div>
                <div className="agent-form-actions">
                  <button
                    className="agent-btn-primary"
                    onClick={() => editingConnection ? handleUpdateConnection(editingConnection) : handleAddConnection()}
                  >
                    {editingConnection ? 'Update' : 'Add'}
                  </button>
                  <button
                    className="agent-btn-secondary"
                    onClick={() => {
                      setShowAddConnection(false);
                      setEditingConnection(null);
                    }}
                  >
                    Cancel
                  </button>
                </div>
              </div>
            )}

            {/* Connections List */}
            {connectionsLoading ? (
              <div className="agent-loading">Loading connections...</div>
            ) : connections.length === 0 ? (
              <div className="agent-empty">
                <SmartToyIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                <p>No agent connections configured.</p>
                <p className="agent-empty-hint">Add a connection to start using agents with your knods.</p>
              </div>
            ) : (
              <div className="agent-connections-list">
                {connections.map(conn => (
                  <div key={conn.id} className="agent-connection-card" style={editingConnection === conn.id ? { display: 'none' } : undefined}>
                    <div className="connection-info">
                      <div className="connection-name">{conn.name}</div>
                      <div className="connection-host">
                        agent: {conn.agent_name || 'iris'}
                      </div>
                      <div className="connection-meta">
                        {conn.pairing_status && (
                          <span className={`pairing-badge pairing-${conn.pairing_status}`}>
                            {conn.pairing_status === 'paired' ? 'Paired' :
                             conn.pairing_status === 'pending' ? 'Pairing pending' : 'Not paired'}
                          </span>
                        )}
                        {conn.last_connected_at && (
                          <span className="connection-last-seen">
                            Last connected: {new Date(conn.last_connected_at).toLocaleDateString()}
                          </span>
                        )}
                      </div>
                    </div>

                    {/* Test result */}
                    {testResults[conn.id] && (
                      <div className={`connection-test-result ${testResults[conn.id].success ? 'success' : 'error'}`}>
                        {testResults[conn.id].success ? (
                          <>
                            <CheckCircleIcon sx={{ fontSize: 14 }} />
                            Connected ({testResults[conn.id].latencyMs}ms)
                          </>
                        ) : (
                          <>
                            <ErrorIcon sx={{ fontSize: 14 }} />
                            {testResults[conn.id].error}
                          </>
                        )}
                      </div>
                    )}

                    <div className="connection-actions">
                      <button
                        className="agent-icon-btn"
                        onClick={() => handleTestConnection(conn.id)}
                        disabled={testingId === conn.id}
                        title="Test connection"
                      >
                        <PlayArrowIcon sx={{ fontSize: 16 }} />
                      </button>
                      <button
                        className="agent-icon-btn"
                        onClick={() => startEditing(conn)}
                        title="Edit"
                      >
                        <EditIcon sx={{ fontSize: 16 }} />
                      </button>
                      <button
                        className="agent-icon-btn danger"
                        onClick={() => handleDeleteConnection(conn.id)}
                        title="Delete"
                      >
                        <DeleteIcon sx={{ fontSize: 16 }} />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* API Keys Tab */}
        {activeTab === 'apikeys' && (
          <div className="agent-settings-section">
            <div className="section-header">
              <div>
                <h2>API Keys</h2>
                <p className="section-description">
                  Generate API keys for headless access to your knods. Keys are shown only once upon creation.
                </p>
              </div>
              <button className="agent-add-btn" onClick={handleCreateKey}>
                <AddIcon sx={{ fontSize: 16 }} />
                Generate Key
              </button>
            </div>

            {/* Create key with name */}
            <div className="api-key-create-row">
              <input
                type="text"
                placeholder="Key name (optional)"
                value={newKeyName}
                onChange={e => setNewKeyName(e.target.value)}
                className="api-key-name-input"
              />
            </div>

            {/* Newly created key */}
            {newKey && (
              <div className="api-key-new-banner">
                <div className="api-key-new-label">
                  Your new API key (copy it now — it won't be shown again):
                </div>
                <div className="api-key-new-value">
                  <code>{newKey.key}</code>
                  <button
                    className="agent-icon-btn"
                    onClick={() => handleCopyKey(newKey.key, 'new')}
                    title="Copy key"
                  >
                    {copiedKeyId === 'new' ? (
                      <CheckCircleIcon sx={{ fontSize: 16, color: 'var(--color-accent-success)' }} />
                    ) : (
                      <ContentCopyIcon sx={{ fontSize: 16 }} />
                    )}
                  </button>
                </div>
              </div>
            )}

            {/* Keys List */}
            {keysLoading ? (
              <div className="agent-loading">Loading API keys...</div>
            ) : apiKeys.length === 0 ? (
              <div className="agent-empty">
                <VpnKeyIcon sx={{ fontSize: 40, opacity: 0.3 }} />
                <p>No API keys generated yet.</p>
              </div>
            ) : (
              <div className="api-keys-list">
                {apiKeys.map(key => (
                  <div key={key.id} className={`api-key-card ${key.revoked_at ? 'revoked' : ''}`}>
                    <div className="api-key-info">
                      <div className="api-key-name">{key.name}</div>
                      <div className="api-key-prefix">
                        <code>{key.key_prefix}...</code>
                      </div>
                      <div className="api-key-meta">
                        Created {new Date(key.created_at).toLocaleDateString()}
                        {key.revoked_at && (
                          <span className="api-key-revoked-badge">Revoked</span>
                        )}
                      </div>
                    </div>
                    {!key.revoked_at && (
                      <div className="api-key-actions">
                        <button
                          className="agent-icon-btn danger"
                          onClick={() => handleRevokeKey(key.id)}
                          title="Revoke key"
                        >
                          <DeleteIcon sx={{ fontSize: 16 }} />
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AgentSettingsPage;
